import { AppointmentView } from "@/Appointments/Components/Calendar/Appointment/AppointmentView";
import { PrivateBillEditSendToPortalPopupStrategy } from "@/Billing/Services/Portal/PrivateBillEditSendToPortalPopupStrategy";
import { SendInvoiceStrategy } from "@/Billing/Services/Portal/Strategies/SendInvoiceStrategy";
import { HistoryTab } from "@/Common/Models/Patient/PatientEnums";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { PatientPrivateBillListItem } from "@/Patient/Models/History/PatientPrivateBillListItem";
import { IPatientUIModel } from "@/Patient/Models/UI/IPatientUIModel";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { BaseSendToPortalPopupStrategy } from "@/Portal/Services/Strategies/BaseSendToPortalPopupStrategy";
import { CommunicationsView } from "./CommunicationsView";
import { ExternalCommunicationsView } from "./ExternalCommunicationsView";
import { MessagesView } from "./MessagesView";
import { PortalCommunicationHistoryView } from "./PortalCommunicationHistoryView";
import { PrivateBillControl } from "./PrivateBillControl";
import { RecallsView } from "./RecallsView";
import { TasksView } from "./TasksView";

interface IPatientHistoryProps {
    patientId: string;
    model: IPatientUIModel;
}

class HistoryView extends React.Component<IPatientHistoryProps, any> {
    public render(): JSX.Element {
        return (
            <div className="inner-container">
                <div className="patient-subtabs" data-cy="historyView-patientSubtabs">
                    <ul>
                        {this.buildHistoryTab(HistoryTab.Appointment, "AppointmentLabel")}
                        {this.buildHistoryTab(HistoryTab.Message, "MessagingLabel")}
                        {this.buildHistoryTab(HistoryTab.Task, "TaskLabel")}
                        {this.buildHistoryTab(HistoryTab.Recall, "RecallLabel")}
                        {cacheStore.ClinicSettings.EnableExternalCommunication && this.buildHistoryTab(HistoryTab.ExternalCommunication, "ExternalCommunicationsLabel")}
                        {cacheStore.ClinicSettings.EnableBidirectionalCommunication && this.buildHistoryTab(HistoryTab.PortalCommunications, "SharedItem_PortalCommunicationsLabel", "portal-communications-tab")}
                        {this.buildHistoryTab(HistoryTab.Communication, "CorrespondenceLabel")}
                        {this.buildHistoryTab(HistoryTab.PrivateBill, "PrivateBillingLabel")}
                    </ul>
                </div>
                <div className="scrollable-part">
                    {this.switchTabComponent(this.props.model.get("PatientHistoryActiveTab"))}
                </div>
            </div>
        );
    }

    private buildHistoryTab(tab: HistoryTab, labelKey: string, dataCy: string = null): JSX.Element {
        var css = this.props.model.get("PatientHistoryActiveTab") === tab ? "active" : null;
        return <li className={css} data-cy={dataCy}><a onClick={this.onTabClick.bind(this, tab)}>{cacheStore.GetString(labelKey)}</a></li>;
    }

    private onTabClick(tab: HistoryTab): void {
        var operation = new LocalEvent(LocalPatientOperations.History.ChangeHistoryTab);
        operation.AddItem("value", tab);
        LocalEventDispatcher.dispatch(operation);
    }

    private getSelectedPrivateBill(): PatientPrivateBillListItem {
        const model = patientStore.PatientHistoryBillsModels[this.props.patientId];

        return model.get("PrivateBillList")
            .filter(x => x.Model.PrivateBillId === model.get("SelectedPrivateBillId"))
            .first();
    }

    private isPrivateBillRecipientEnabledAndBillEditable(): boolean {
        const selectedBill = this.getSelectedPrivateBill();
        if (!selectedBill)
            return false;

        return cacheStore.ClinicSettings.EnablePrivateBillRecipient
            && !selectedBill.Model?.IsLockedForEditing
            && !selectedBill.Model?.IsClosed;
    }

    private switchTabComponent(current: HistoryTab): JSX.Element {
        switch (current) {
            case HistoryTab.Appointment:
            {
                let model = patientStore.PatientHistoryAppointmentsModels[this.props.patientId];
                return <AppointmentView Model={model} />;
            }
            case HistoryTab.Message:
            {
                let model = patientStore.PatientHistoryMessagesModels[this.props.patientId];
                return <MessagesView Model={model} />;
            }

            case HistoryTab.Task:
            {
                let model = patientStore.PatientHistoryTasksModels[this.props.patientId];
                return <TasksView Model={model} />;
            }

            case HistoryTab.Communication:
            {
                let model = patientStore.PatientHistoryCommunicationsModels[this.props.patientId];
                return (
                    <CommunicationsView
                        PatientModel={patientStore.PatientModels[this.props.patientId]}
                        Model={model}
                    />
                );
            }

            case HistoryTab.Recall:
            {
                let model = patientStore.PatientHistoryRecallsModels[this.props.patientId];
                return <RecallsView Model={model} />;
            }

            case HistoryTab.PrivateBill:
            {
                let model = patientStore.PatientHistoryBillsModels[this.props.patientId];
                let billableServicesModel = patientStore.PatientHistoryBillableServicesModels[this.props.patientId];

                const patientModel = patientStore.PatientModels[this.props.patientId];
                const sendToPortalDefaultValue = this.isPrivateBillRecipientEnabledAndBillEditable()
                    ? PrivateBillEditSendToPortalPopupStrategy.getDefaultValueForSyncToPortalModel(
                        patientModel.IndividualResponse.PortalSyncState,
                        patientModel.IndividualResponse.Contact_id,
                        new Date(model.get("DateOfVisit"))
                    )
                    : model.get("PortalLinkedItemModel");

                return (
                    <div>
                        {model.get("IsPortalSyncPopupOpen") && (
                            <SendToPortalPopup
                                model={sendToPortalDefaultValue}
                                strategy={this.getSendToPortalStrategy()}
                            />
                        )}

                        <PrivateBillControl
                            Model={model}
                            BillableServicesModel={billableServicesModel}
                            PrintOp={LocalPatientOperations.History.PrivateBill_Print}
                            CloseInvalidOp={LocalPatientOperations.History.PrivateBill_CloseInvalidPopup}
                            SaveInvalidationOp={LocalPatientOperations.History.PrivateBill_SaveInvalidation}
                            InvalidateOp={LocalPatientOperations.History.PrivateBill_InvalidateBill}
                            InvalidationChangeOp={LocalPatientOperations.History.PrivateBill_NoteInvalidationChange}
                            PatientModel={patientModel}
                            IsPopup={false}
                            DisableButtons={false}
                            OnButtonClick={null}
                        />
                    </div>
                );
            }

            case HistoryTab.ExternalCommunication:
                return <ExternalCommunicationsView />;

            case HistoryTab.PortalCommunications:
                return (
                    <PortalCommunicationHistoryView
                        patient={patientStore.getCurrentPatient()}
                    />
                );

            default:
                return <div />;
        }
    }

    private getSendToPortalStrategy(): BaseSendToPortalPopupStrategy {
        const selectedPrivateBill = this.getSelectedPrivateBill();

        if (this.isPrivateBillRecipientEnabledAndBillEditable()) {
            return new PrivateBillEditSendToPortalPopupStrategy(
                () => sessionStore.dispatch(LocalPatientOperations.History.PrivateBill_CloseSendToPortalPopup),
                patientStore.getCurrentPatientContactId(),
                false,
                selectedPrivateBill?.Model.PaymentMethod
            );
        }
        else {
            return new SendInvoiceStrategy(cacheStore.ClinicSettings.EnablePrivateBillRecipient
                ? selectedPrivateBill?.Model.IsClosed || selectedPrivateBill?.Model.IsLockedForEditing
                : selectedPrivateBill?.Model.IsClosed, selectedPrivateBill?.Model.PaymentMethod);
        }
    }
}

export { HistoryView };
